import { useCallback, useEffect, useState } from 'react'

import { IAlcatrazTicketRow } from '@/common/models/settings'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Tabs, TabsList, TabsTrigger } from '@/components/ui'
import Loader from '../../../common/components/Loader'
import { IColumn } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deleteAlcatrazTicket,
  fixAlcatrazTickets,
  getAlcatrazTickets,
  registerAlcatrazTicket,
} from '../../../core/services/festival-settings'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { AlcatrazTicketsTable } from './AlcatrazTicketsTable'

export const AlcatrazTicketsPage: React.FC = () => {
  const {
    page,
    setPage,
    sortBy,
    setSortBy,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    extraParams,
    setExtraParams,
  } = useTableParams({
    extraParams: {
      key: 'alcatraz-current',
    },
  })

  const { hasEditPermission } = useCheckPermission({ page: 'ORDERS' })

  const [view, setView] = useState<'current' | 'passed'>(
    (extraParams.key as 'current' | 'passed') || 'current',
  )
  const [problemsOnly, setProblemsOnly] = useState<boolean>(false)
  const [rows, setRows] = useState<IAlcatrazTicketRow[]>([])
  const [loading, setLoading] = useState(true)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const getAlcatrazTicketsData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAlcatrazTickets({
        view,
        problemsOnly,
        page,
        size,
      })
      if (response.status === 200) {
        setRows(response.data.content)
        setTotalElemenets(response.data.totalElements)
        setSize(response.data.size)
        setPage(response.data.pageNumber + 1)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, view, problemsOnly, setPage, setSize, setTotalElemenets])

  useEffect(() => {
    getAlcatrazTicketsData()
  }, [getAlcatrazTicketsData])

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSearchParams = useCallback(
    async (_col: IColumn, _q: string | null) => {
      // Search is not supported by the alcatraz-tickets endpoint.
    },
    [],
  )

  const handleRegister = async (row: IAlcatrazTicketRow) => {
    if (!hasEditPermission) return
    try {
      setLoading(true)
      const resp = await registerAlcatrazTicket(row.participationId)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Alcatraz enrollment has been triggered.')
        await getAlcatrazTicketsData()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (row: IAlcatrazTicketRow) => {
    if (!hasEditPermission) return
    try {
      setLoading(true)
      await deleteAlcatrazTicket(row.participationId)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Ticket has been deleted.')
      await getAlcatrazTicketsData()
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFixIssues = async () => {
    if (!hasEditPermission) return
    try {
      setLoading(true)
      const resp = await fixAlcatrazTickets(view)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage(`Fixed ${resp.data} issue(s).`)
        await getAlcatrazTicketsData()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Header pageTitle="Alcatraz Tickets" />
      {loading && <Loader />}

      <Tabs
        value={view}
        onValueChange={(newKey) => {
          setView(newKey as 'current' | 'passed')
          setExtraParams((s) => ({ ...s, key: newKey }))
          setPage(1)
        }}
        className="w-full"
      >
        <TabsList className="grid w-full mb-6">
          <div className="bg-white/10 p-2.5 rounded-[20px]">
            <TabsTrigger value="current">Current</TabsTrigger>
            <TabsTrigger value="passed">Passed</TabsTrigger>
          </div>
        </TabsList>
        <Card className="!bg-monochrome-4-60 p-5 backdrop-blur-lg max-h-[calc(100svh-280px)] overflow-hidden">
          <div className="flex items-center gap-4 justify-between flex-wrap">
            <PageTitle
              title="Alcatraz Tickets"
              icon="profile-2user"
              className="mb-5"
            />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={problemsOnly}
                  onCheckedChange={() => {
                    setProblemsOnly(!problemsOnly)
                    setPage(1)
                  }}
                />
                <div className="text-body-medium text-monochrome-1">
                  Show problems
                </div>
              </div>
              {problemsOnly && hasEditPermission && (
                <Button size="sm" onClick={handleFixIssues}>
                  Fix issues
                </Button>
              )}
            </div>
          </div>

          <AlcatrazTicketsTable
            maxHeight="calc(100vh - 380px)"
            handleSearchParams={handleSearchParams}
            hasEditPermission={hasEditPermission}
            sortBy={sortBy}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortDirection={sortDirection}
            rows={rows}
            loading={loading}
            onRegister={handleRegister}
            onDelete={handleDelete}
            noDataMessage={<span>Sorry, There are no data</span>}
          />
        </Card>
      </Tabs>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
